import { useState, useRef } from 'react'
import { Upload, FileText, FolderOpen, PenLine, Sparkles } from 'lucide-react'
import { api } from '@/lib/api'
import { notify } from '@/lib/notify'
import type { StudioTemplate } from '@/lib/api'
import { StudioTemplateSelector } from './StudioTemplateSelector'

type InputTab = 'paste' | 'upload' | 'solution' | 'editor'

interface Props {
  onSessionCreated: (sessionId: string) => void
}

export function StudioInput({ onSessionCreated }: Props) {
  const [tab, setTab] = useState<InputTab>('paste')
  const [text, setText] = useState('')
  const [title, setTitle] = useState('')
  const [template, setTemplate] = useState<StudioTemplate | null>('professional_report')
  const [loading, setLoading] = useState(false)
  const [solutions, setSolutions] = useState<{ id: string; title: string }[]>([])
  const [editorSessions, setEditorSessions] = useState<{ id: string; documentTitle: string | null }[]>([])
  const [selectedSolutionId, setSelectedSolutionId] = useState('')
  const [selectedEditorId, setSelectedEditorId] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const loadSolutions = async () => {
    try {
      const res = await fetch('/api/solutions?limit=50').then(r => r.json())
      setSolutions((res.solutions || []).map((s: any) => ({ id: s.id, title: s.title })))
    } catch { /* ignore */ }
  }

  const loadEditorSessions = async () => {
    try {
      const res = await api.editor.sessions.list(50)
      setEditorSessions((res.sessions || []).map((s: any) => ({ id: s.id, documentTitle: s.documentTitle })))
    } catch { /* ignore */ }
  }

  const handleTabChange = (t: InputTab) => {
    setTab(t)
    if (t === 'solution' && solutions.length === 0) loadSolutions()
    if (t === 'editor' && editorSessions.length === 0) loadEditorSessions()
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setLoading(true)
    try {
      const result = await api.studio.extractText(file)
      setText(result.text)
      setTitle(file.name.replace(/\.[^.]+$/, ''))
      setTab('paste')
      notify.success(`Extracted text from ${file.name}`)
    } catch (err) {
      notify.error('Failed to extract text', { description: err instanceof Error ? err.message : String(err) })
    } finally {
      setLoading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleImportSolution = async () => {
    if (!selectedSolutionId) return
    setLoading(true)
    try {
      const result = await api.studio.importFromSolution(selectedSolutionId)
      setText(result.text)
      setTitle(result.title)
      setTab('paste')
      notify.success(`Imported: ${result.title}`)
    } catch (err) {
      notify.error('Import failed', { description: err instanceof Error ? err.message : String(err) })
    } finally {
      setLoading(false)
    }
  }

  const handleImportEditor = async () => {
    if (!selectedEditorId) return
    setLoading(true)
    try {
      const result = await api.studio.importFromEditorSession(selectedEditorId)
      setText(result.text)
      setTitle(result.title)
      setTab('paste')
      notify.success(`Imported: ${result.title}`)
    } catch (err) {
      notify.error('Import failed', { description: err instanceof Error ? err.message : String(err) })
    } finally {
      setLoading(false)
    }
  }

  const handleBeautify = async () => {
    if (!text.trim()) {
      notify.error('Please provide document text')
      return
    }
    setLoading(true)
    try {
      const session = await api.studio.sessions.create({
        inputText: text,
        inputTitle: title || undefined,
        template: template || undefined,
        sourceType: 'paste',
      })
      onSessionCreated(session.id)
    } catch (err) {
      notify.error('Failed to create session', { description: err instanceof Error ? err.message : String(err) })
      setLoading(false)
    }
  }

  const TABS: { id: InputTab; label: string; icon: typeof FileText }[] = [
    { id: 'paste', label: 'Paste / Type', icon: FileText },
    { id: 'upload', label: 'Upload File', icon: Upload },
    { id: 'solution', label: 'From Solution', icon: FolderOpen },
    { id: 'editor', label: 'From Editor', icon: PenLine },
  ]

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Document Studio</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Feed in a document, choose a template, and watch it transform
        </p>
      </div>

      {/* Input tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-200 dark:border-white/10">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => handleTabChange(id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              tab === id
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Input area */}
      <div className="mb-6">
        {tab === 'paste' && (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Document title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-surface-800 text-gray-900 dark:text-white placeholder:text-gray-400"
            />
            <textarea
              placeholder="Paste your document content here (markdown, plain text, or HTML)..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={14}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-surface-800 text-gray-900 dark:text-white placeholder:text-gray-400 font-mono resize-y"
            />
            {text && (
              <p className="text-xs text-gray-400">
                {text.split(/\s+/).filter(Boolean).length} words
              </p>
            )}
          </div>
        )}

        {tab === 'upload' && (
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-gray-300 dark:border-white/20 rounded-lg p-12 text-center cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-400 transition-colors"
          >
            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-3" />
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
              Click to upload a document
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Supports .md, .txt, .docx, .pdf, .csv, .xlsx
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".md,.txt,.docx,.pdf,.csv,.xlsx,.pptx"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        )}

        {tab === 'solution' && (
          <div className="space-y-3">
            <select
              value={selectedSolutionId}
              onChange={(e) => setSelectedSolutionId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-surface-800 text-gray-900 dark:text-white"
            >
              <option value="">Select a saved solution...</option>
              {solutions.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
            <button
              onClick={handleImportSolution}
              disabled={!selectedSolutionId || loading}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Import Solution
            </button>
          </div>
        )}

        {tab === 'editor' && (
          <div className="space-y-3">
            <select
              value={selectedEditorId}
              onChange={(e) => setSelectedEditorId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-surface-800 text-gray-900 dark:text-white"
            >
              <option value="">Select an editor session...</option>
              {editorSessions.map((s) => (
                <option key={s.id} value={s.id}>{s.documentTitle || 'Untitled'}</option>
              ))}
            </select>
            <button
              onClick={handleImportEditor}
              disabled={!selectedEditorId || loading}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Import from Editor
            </button>
          </div>
        )}
      </div>

      {/* Template selection */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Choose a template style
        </h3>
        <StudioTemplateSelector selected={template} onSelect={setTemplate} />
      </div>

      {/* Beautify button */}
      <div className="flex justify-center">
        <button
          onClick={handleBeautify}
          disabled={!text.trim() || loading}
          className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/25 transition-all"
        >
          <Sparkles className="w-4 h-4" />
          {loading ? 'Creating session...' : 'Beautify Document'}
        </button>
      </div>
    </div>
  )
}
